/**
 * conversion-playbooks.js
 *
 * Industry-specific funnel walkthroughs for "The Conversion Checklist."
 * Each playbook describes what should happen when someone lands on a
 * client's website, the most common mistakes in that industry, and one
 * quick win they can ship this week.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * HOW TO ADD A NEW INDUSTRY:
 *   1. Add a new object to the PLAYBOOKS array below.
 *   2. `matchers` is a list of keywords (lowercased) used to auto-detect
 *      which playbook to use from the client.industry string. Add any
 *      variations the agency is likely to type (e.g., "hvac", "plumbing",
 *      "home services", "contractor" all map to the same playbook).
 *   3. `scenarioLabel` is the noun used in the opener — e.g., "restaurant"
 *      in "When someone lands on a restaurant site…".
 *   4. `steps` should stay at 5 entries. Each has a title (action verb)
 *      and a copy block (1–2 sentences).
 *   5. `biggestLeak` is the ONE thing agencies see this industry get wrong
 *      most often.
 *   6. `quickWin` is the fastest thing the client can change this week.
 *
 * The GENERIC playbook is the fallback when no matcher hits.
 * ─────────────────────────────────────────────────────────────────────────
 */

export const PLAYBOOKS = [
  {
    id: "restaurant",
    label: "Restaurants & Food",
    matchers: ["restaurant", "food", "cafe", "café", "bar", "bakery", "coffee", "kitchen", "dining", "eatery", "catering"],
    scenarioLabel: "restaurant",
    steps: [
      {
        title: "They see food, not a logo",
        copy: "The hero image should be an appetizing photo of your actual menu. Not a stock shot, not a generic patio. People eat with their eyes before they read a single word.",
      },
      {
        title: "They find the four things they came for",
        copy: "Menu, Hours, Location, Phone: visible within the first screen. If someone has to scroll or hunt, you just lost the order to the restaurant ranked above you on Google.",
      },
      {
        title: "They see that other people love you",
        copy: "Pull recent Google or Yelp reviews onto the homepage. Show the star rating. Show a photo from a real guest. Social proof converts visitors who are on the fence between you and the place next door.",
      },
      {
        title: "They take one clear action",
        copy: "One big, thumb-friendly button that matches how people actually use you: Order Online, Reserve a Table, Call, or Get Directions. Not all four equally: one should dominate.",
      },
      {
        title: "They leave with a way back",
        copy: "Click-to-call on mobile so the number ends up in their recents. An email signup for specials. A follow button for Instagram. Every visit should leave a trail.",
      },
    ],
    biggestLeak:
      "A beautiful hero photo of an empty dining room instead of the food. Interior photos sell ambiance; food photos sell orders.",
    quickWin:
      "Add a sticky \"Order Now\" or \"Call\" button that stays at the bottom of the screen on mobile. Expect a 10-20% lift in taps to conversion.",
  },

  {
    id: "events",
    label: "Events, Venues & Wineries",
    matchers: ["event", "venue", "winery", "wedding", "vineyard", "meeting", "conference", "banquet", "hospitality"],
    scenarioLabel: "venue",
    steps: [
      {
        title: "They see the experience, not the building",
        copy: "Lead with photography of real events you've hosted: people laughing, a set table, the first dance, a toast. Couples and corporate planners are buying a feeling, not a floor plan.",
      },
      {
        title: "They know what you host",
        copy: "Clear categories on the homepage: Weddings, Corporate Retreats, Tastings, Private Parties. Planners bounce within seconds if they can't tell whether you serve their use case.",
      },
      {
        title: "They see proof from people like them",
        copy: "Testimonials from recent couples or company names that have booked you. A couple's Instagram photo, not a stock testimonial quote.",
      },
      {
        title: "They can picture their event here",
        copy: "Capacity numbers, pricing tier or \"starting at\" cost, and a gallery organized by event type. Vague pricing is the #1 reason planners move on to your competitor.",
      },
      {
        title: "They take one next step",
        copy: "One dominant CTA: \"Check Availability\" or \"Request a Tour\": leading to a short, friendly form (name, event date, event type). No 12-field intake forms on first contact.",
      },
    ],
    biggestLeak:
      "Hiding pricing entirely. \"Contact us for pricing\" signals expensive AND high-friction. Showing a starting-at number filters out non-buyers and builds trust with the rest.",
    quickWin:
      "Add a single sentence on the pricing page: \"Full-weekend bookings start at $X,XXX. Weekday & off-season rates available.\" Planners will reward the transparency with a call.",
  },

  {
    id: "home-services",
    label: "Home Services & Contractors",
    matchers: ["home services", "contractor", "hvac", "plumbing", "plumber", "electric", "electrician", "roofing", "roof", "construction", "handyman", "pest", "cleaning", "solar"],
    scenarioLabel: "home services",
    steps: [
      {
        title: "They know you serve their area",
        copy: "The city or ZIP code should be in the hero or right below it. If they don't see their town in the first 3 seconds, they hit back and call the next result.",
      },
      {
        title: "They trust that you'll actually show up",
        copy: "Licensed. Insured. Years in business. BBB or industry certification logos. These aren't decoration: they're the difference between a call and a bounce.",
      },
      {
        title: "They see real work on real homes",
        copy: "Before/after photos, not stock photos. Reviews that name actual neighborhoods (\"the Smiths in Keswick\") are 10x more trusted than generic 5-star widgets.",
      },
      {
        title: "They can call you right now",
        copy: "A click-to-call button at thumb height on mobile. No contact form required. When someone's basement is flooding, they're not filling out forms.",
      },
      {
        title: "They can book for non-emergencies online",
        copy: "For tune-ups, inspections, and estimates: a simple scheduler. Fewer phone tag loops for you, faster answers for them.",
      },
    ],
    biggestLeak:
      "A contact form as the only way to reach you. Homeowners with urgent problems don't fill out forms: they call the first place with a visible phone number.",
    quickWin:
      "Put your phone number, big and tappable, at the top of every page (not hidden in the nav menu). Emergencies drive immediate calls.",
  },

  {
    id: "landscaping",
    label: "Landscaping & Outdoor",
    matchers: ["landscap", "lawn", "garden", "outdoor", "tree", "hardscape", "irrigation", "pool", "patio"],
    scenarioLabel: "landscaping",
    steps: [
      {
        title: "They see your work, not stock gardens",
        copy: "A gallery of finished projects you actually did, organized by type (residential yard, commercial property, hardscape, garden design). Stock photos kill credibility instantly.",
      },
      {
        title: "They understand what you do",
        copy: "A clear services list: lawn care, design, hardscape, tree work, irrigation. Most visitors land with one specific job in mind and need to see it named.",
      },
      {
        title: "They see your service radius",
        copy: "\"Serving Charlottesville and surrounding counties\" on the homepage. Homeowners won't call if they're not sure you drive to them.",
      },
      {
        title: "They request a free estimate",
        copy: "A simple form: name, address, what they need. 3–5 fields max. Or a click-to-call number for same-day estimates.",
      },
      {
        title: "They book for the next season",
        copy: "Seasonal CTAs: \"Book your Spring cleanup\" in February, \"Reserve your Fall cleanup\" in August. Calendar-aware homepages book out faster.",
      },
    ],
    biggestLeak:
      "Only one gallery with every project type mixed together. Homeowners browsing for a patio don't want to scroll past a lawn-mowing photo to find it.",
    quickWin:
      "Split your gallery into 3–4 filtered categories and label them clearly. Visitors find their vision 3x faster and feel understood.",
  },

  {
    id: "nonprofit",
    label: "Non-profit & Community",
    matchers: ["non-profit", "nonprofit", "charity", "community", "housing", "foundation", "ministry", "humanitarian", "volunteer"],
    scenarioLabel: "non-profit",
    steps: [
      {
        title: "They understand the mission in one sentence",
        copy: "Your hero headline is not your tagline: it's the one-line answer to \"what problem do you solve and for whom?\" If it takes a paragraph, rewrite it.",
      },
      {
        title: "They see impact, not intentions",
        copy: "\"Families helped this year: 347.\" \"Homes repaired: 1,200 since 2010.\" Numbers make abstract mission concrete, and concrete wins donations.",
      },
      {
        title: "They see who you help",
        copy: "A real story: a name, a photo, a before-and-after. Donors give to people, not to causes. One good story outperforms a page of statistics.",
      },
      {
        title: "They have three clear doors",
        copy: "Donate · Volunteer · Get Help. These three buttons, always visible. Visitors self-sort into the role they're here to play.",
      },
      {
        title: "They stay in the loop",
        copy: "Newsletter signup with a specific promise (\"Monthly impact updates, 2-minute read\"). An events calendar. Ways to follow even if they can't give today.",
      },
    ],
    biggestLeak:
      "A homepage built around the organization instead of the people served. Donors want to meet a family, not read your history page.",
    quickWin:
      "Move one real beneficiary story above the fold on the homepage, with a photo and a name. Donations tend to follow empathy, not mission statements.",
  },

  {
    id: "retail-ecommerce",
    label: "Retail & E-commerce",
    matchers: ["retail", "ecommerce", "e-commerce", "shop", "store", "boutique", "apparel", "clothing", "gift", "merchandise"],
    scenarioLabel: "online store",
    steps: [
      {
        title: "They see products, not marketing copy",
        copy: "Hero featuring best-sellers, new arrivals, or the current collection. Shoppers came to see things: give them things to see in the first scroll.",
      },
      {
        title: "They trust the transaction",
        copy: "Review count and rating visible on product pages. Free shipping / easy returns badges. Secure checkout lock icons. These are table stakes, not upgrades.",
      },
      {
        title: "They find their size, fit, or variant fast",
        copy: "Clear filters, size guides, in-stock status. Ambiguity kills carts. \"Usually fits true to size\" converts better than a silent size chart.",
      },
      {
        title: "They move to cart with no friction",
        copy: "Add-to-cart should confirm without a full page reload. \"Keep shopping\" and \"Checkout\" both obvious. Surprise shipping costs at checkout is the #1 cart-killer.",
      },
      {
        title: "They finish checkout",
        copy: "Guest checkout is non-negotiable. Apple Pay / Shop Pay / Google Pay reduce form friction. Trust badges right at the payment step seal the deal.",
      },
    ],
    biggestLeak:
      "Forcing account creation before checkout. Every required account costs sales: guest checkout with optional signup after the order converts significantly better.",
    quickWin:
      "Show shipping cost (or \"free shipping on $X+\") before checkout: ideally in the cart. Eliminating the checkout-stage surprise reduces abandonment dramatically.",
  },

  {
    id: "healthcare",
    label: "Healthcare & Medical",
    matchers: ["health", "medical", "clinic", "doctor", "dental", "dentist", "wellness center", "hospital", "physician", "therapy", "chiropract"],
    scenarioLabel: "healthcare",
    steps: [
      {
        title: "They find their provider or specialty",
        copy: "Clear categories: family medicine, pediatrics, cardiology, etc. Search-by-condition also helps (\"I have back pain\" → back to the right provider).",
      },
      {
        title: "They see that you take their insurance",
        copy: "An insurance list on the homepage or one click away. Uncertainty here is the top reason patients call a competing practice.",
      },
      {
        title: "They book online without calling",
        copy: "A scheduling widget, even for a free 15-minute consult. Modern patients expect this: phone-only booking is a 2010s experience.",
      },
      {
        title: "They see credentials and patient voices",
        copy: "Board certifications, hospital affiliations, years in practice, AND real patient reviews. Credentials build trust, reviews build warmth.",
      },
      {
        title: "They know what the first visit looks like",
        copy: "A \"New Patient\" page with forms to download, what to bring, parking info, approximate wait times. Clarity reduces no-shows.",
      },
    ],
    biggestLeak:
      "A generic stock-photo homepage. Patients pick providers partly on vibe: showing your actual team and actual waiting room converts better than a smiling model.",
    quickWin:
      "Replace the top stock photo with a real team photo or a short video walkthrough of the office. It humanizes the practice in one scroll.",
  },

  {
    id: "real-estate",
    label: "Real Estate",
    matchers: ["real estate", "realtor", "property", "realty", "broker", "homes for sale", "mls"],
    scenarioLabel: "real estate",
    steps: [
      {
        title: "They search by area or map",
        copy: "A neighborhood grid or an embedded map on the homepage. Buyers start with a place, not an agent. Meet them where their head is.",
      },
      {
        title: "They browse recent listings with great photos",
        copy: "Fresh inventory, high-quality photos, accurate prices. Stale listings (sold but still showing) kill your credibility more than you think.",
      },
      {
        title: "They meet you (the agent)",
        copy: "Face, name, reviews, local expertise. Real estate is a relationship business: buyers are choosing a human, not just a portal.",
      },
      {
        title: "They contact you the way they prefer",
        copy: "\"Text me about this property\" is as important as call or email. Modern buyers text. The form on every listing should be 3 fields, not 10.",
      },
      {
        title: "They stay in the loop",
        copy: "Save searches, new-listing alerts, neighborhood reports. Buyers often take 3–6 months: keep showing up in their inbox or you lose them to the next agent.",
      },
    ],
    biggestLeak:
      "Generic agent bio with years of experience but no neighborhood specifics. Buyers want someone who knows THEIR street, not a résumé.",
    quickWin:
      "Add 2–3 sentences to your bio about the specific neighborhoods you serve and why you love them. Specificity converts browsers to leads.",
  },

  {
    id: "professional-services",
    label: "Professional Services",
    matchers: ["law", "legal", "attorney", "consulting", "consultant", "accounting", "accountant", "cpa", "financial", "advisor", "agency", "marketing", "design", "architect"],
    scenarioLabel: "professional services",
    steps: [
      {
        title: "They know what problem you solve",
        copy: "\"We help mid-sized law firms collect more.\" Not \"innovative financial solutions.\" Plain-English outcome statements win against jargon every time.",
      },
      {
        title: "They see who you serve",
        copy: "Industries, company size, stage of business. If a visitor can't self-identify as your ideal client in 10 seconds, they leave.",
      },
      {
        title: "They see results and case studies",
        copy: "Numbers, logos (with permission), one-paragraph case studies. Credibility in this category is built on proof of prior work, not promises.",
      },
      {
        title: "They book a low-commitment consultation",
        copy: "A free 15-minute call beats a contact form every time. Pair it with an online scheduler so they can book without playing email tag.",
      },
      {
        title: "They download something to stay warm",
        copy: "A guide, checklist, or template that solves a real slice of their problem. This is the top-of-funnel lead magnet that feeds the nurture email sequence.",
      },
    ],
    biggestLeak:
      "\"Contact us\" as the only conversion option. High-consideration buyers want to learn before they talk: a guide or free assessment moves 10x more of them into your funnel.",
    quickWin:
      "Create one downloadable resource (a 1-page checklist or mini-guide) and put it behind an email opt-in on the homepage. Start building the nurture list this week.",
  },

  {
    id: "fitness",
    label: "Fitness & Wellness",
    matchers: ["fitness", "gym", "yoga", "pilates", "crossfit", "studio", "wellness", "spa", "salon", "barre"],
    scenarioLabel: "fitness studio",
    steps: [
      {
        title: "They see people like them",
        copy: "Real members, all ages, body types, skill levels, not airbrushed stock athletes. Intimidation is the top reason someone doesn't book their first class.",
      },
      {
        title: "They get the vibe",
        copy: "A 30-second video walkthrough or interior photos. Prospects are quietly asking \"will I feel weird here?\" Answer visually before they ask out loud.",
      },
      {
        title: "They know when you're open",
        copy: "An embedded class schedule, right on the homepage. Booking apps are great, but the first-time visitor needs to see availability without installing anything.",
      },
      {
        title: "They start a trial",
        copy: "A free class or a 7-day pass, one click to claim. Friction at this step kills memberships: make it a no-excuses offer.",
      },
      {
        title: "They know what to expect",
        copy: "A \"First Timer?\" page: what to wear, what to bring, how early to arrive, who will greet them. Removes anxiety, raises show-up rate.",
      },
    ],
    biggestLeak:
      "A website that shows equipment and empty rooms instead of members. The product isn't the equipment: it's the community.",
    quickWin:
      "Get 3 permission-granted photos of actual members mid-class and replace the stock imagery on your homepage. Conversion on trial-class bookings will climb.",
  },

  {
    id: "education",
    label: "Education & Training",
    matchers: ["school", "education", "academy", "tutor", "course", "training", "coaching", "university", "college", "learning"],
    scenarioLabel: "school",
    steps: [
      {
        title: "They see who it's for",
        copy: "Grade level, age range, subject, experience level: clearly stated. Parents and learners self-select fast, and you want to be welcoming to the right ones.",
      },
      {
        title: "They see outcomes, not curriculum",
        copy: "\"92% of our students improved their SAT score by 150 points.\" \"Alumni attend Ivy-plus schools at 3x the national rate.\" Outcomes win over syllabus lists.",
      },
      {
        title: "They understand the program",
        copy: "Duration, schedule, cost (or tuition range), format (online/in-person/hybrid). Vagueness here costs you the next step.",
      },
      {
        title: "They schedule a tour or info session",
        copy: "One click to book a campus visit or virtual info session. Scheduling widget beats \"fill out this form and we'll get back to you.\"",
      },
      {
        title: "They take the path to enrollment",
        copy: "A clear \"Apply\" or \"Enroll\" path, with a timeline (application due, acceptance date, start date). Vague next steps = abandoned applications.",
      },
    ],
    biggestLeak:
      "A website that reads like a brochure instead of a storyteller. Show a current student's day or an alum's outcome: narrative beats bullet points.",
    quickWin:
      "Add one alum or graduate story to the homepage: a photo, a name, a one-paragraph \"here's where I am now.\" Conversion on info sessions climbs.",
  },

  {
    id: "automotive",
    label: "Automotive",
    matchers: ["auto", "automotive", "car", "dealership", "dealer", "motorcycle", "truck", "vehicle", "mechanic", "repair"],
    scenarioLabel: "dealership",
    steps: [
      {
        title: "They see real inventory or real service",
        copy: "For dealers, recent photos of your actual cars, with clear prices. For repair, a services list with transparent starting prices. Generic car photos kill trust.",
      },
      {
        title: "They get transparency up front",
        copy: "Prices, not \"Call for Price.\" Trade-in calculators. Service rate cards. Buyers have been burned by opaque pricing: reward their research.",
      },
      {
        title: "They see why you're trustworthy",
        copy: "Manufacturer certifications, ASE-certified technicians, decades in business, Google reviews by the thousands. Automotive is a trust category: overload on credibility signals.",
      },
      {
        title: "They schedule online",
        copy: "Book a service appointment or a test drive without calling. Real-time availability widgets convert 2–3x better than \"we'll call you back.\"",
      },
      {
        title: "They get a quote or financing estimate",
        copy: "Trade-in value tool. Financing pre-qual. Repair estimate by make/model. Tools move visitors from browsing to buying.",
      },
    ],
    biggestLeak:
      "Hiding prices. Every visitor is already price-shopping on Carfax, Carvana, or a competing shop: opacity just sends them to the competitor who's transparent.",
    quickWin:
      "List your 5 most common services (or trade-in process) with starting prices, on one page. Expect measurably more booked appointments.",
  },

  // GENERIC FALLBACK — used when no matcher hits.
  {
    id: "generic",
    label: "Your Business",
    matchers: [],
    scenarioLabel: "your",
    steps: [
      {
        title: "They understand what you do in 5 seconds",
        copy: "Your hero headline should answer \"what problem do I solve, for whom?\" in plain English. Not a tagline, not a mission: an outcome.",
      },
      {
        title: "They find what they came for",
        copy: "Your top three pages should be clickable from the hero. Visitors came with a goal: don't make them hunt through a mega-menu.",
      },
      {
        title: "They build trust in you",
        copy: "Reviews. Recent work. The team. Years in business. Pick the 2 strongest trust signals you have and make them visible above the fold.",
      },
      {
        title: "They take one clear next step",
        copy: "One dominant CTA that matches how you actually earn revenue: Call, Book, Quote, Buy, Apply. Every other button should be visibly secondary.",
      },
      {
        title: "They leave with a way back to you",
        copy: "Email opt-in, social follow, click-to-call (on mobile, your number ends up in their recents). Every visitor who doesn't convert today is a potential convert tomorrow.",
      },
    ],
    biggestLeak:
      "A homepage with too many equally-sized calls-to-action. When everything is important, nothing is: visitors freeze and leave.",
    quickWin:
      "Pick ONE primary action your homepage should drive. Style that button to be visually dominant. Make every other button look secondary.",
  },
];

/**
 * Resolve the right playbook for a client.
 * @param {string} industry - the client.industry string (can be blank).
 * @returns {object} the matching playbook, falling back to generic.
 */
export function getPlaybookForIndustry(industry) {
  const lower = (industry || "").toLowerCase().trim();
  if (!lower) return PLAYBOOKS.find(p => p.id === "generic");
  for (const p of PLAYBOOKS) {
    if (p.id === "generic") continue;
    if (p.matchers.some(m => lower.includes(m))) return p;
  }
  return PLAYBOOKS.find(p => p.id === "generic");
}
